import { ConfigProvider } from "antd";
import { useApplicationViewSettings } from "../../../../../containers/ApplicationPage/ApplicationViewContext";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Descriptions, Space } from "antd";
import { useSelector } from "react-redux";
import { DeleteOutlined, PaperClipOutlined } from "@ant-design/icons";
import _ from "lodash";
import { makeSelectHost } from "../../../../../containers/App/selectors";
import getDocumentUrl from "../../../../../utils/getDocumentUrl";
import UploadFileDragger from "../../../../UploadFileDragger";
import { AttachDocumentDataConverter } from "../../../../../viewModels/Documents/AttachDocumentModel";
import type { ApplicationSummaryBlockProps } from "../../types";

const ApplicationDocuments = React.memo(({ application, onChange, canEditApplicationFields, onCommit }: ApplicationSummaryBlockProps) => {
  const { t } = useTranslation("translations");
  const viewSettings = useApplicationViewSettings();
  const host = useSelector(makeSelectHost());
  return (
    <ConfigProvider
      theme={{
        token: { fontSize: 12 },
        components: {
          Select: {
            activeBorderColor: "transparent",
            activeOutlineColor: "transparent",
          },
          Input: {
            activeBorderColor: "transparent",
            activeShadow: "none",
            inputFontSize: 12,
            inputFontSizeSM: 12,
            inputFontSizeLG: 12,
            paddingBlock: 0,
            paddingInline: 0,
            paddingBlockSM: 0,
            paddingInlineSM: 0,
            paddingBlockLG: 0,
            paddingInlineLG: 0,
          },
          InputNumber: {
            activeBorderColor: "transparent",
            activeShadow: "none",
            inputFontSize: 12,
            inputFontSizeSM: 12,
            inputFontSizeLG: 12,
            paddingBlock: 0,
            paddingInline: 0,
            paddingBlockSM: 0,
            paddingInlineSM: 0,
            paddingBlockLG: 0,
            paddingInlineLG: 0,
          },
        },
      }}
      select={{ styles: { root: { padding: 0 } } }}
      textArea={{ styles: { textarea: { minHeight: 22 } } }}
      descriptions={{
        styles: {
          label: { padding: "6px 8px" },
          content: { padding: "6px 8px" },
        },
      }}
    >
      <Descriptions
        bordered
        size="small"
        items={[
          {
            label: t("attached_documents"),
            span: 3,

            styles: { label: { width: 250 } },
            children: (
              <>
                {canEditApplicationFields && (
                  <UploadFileDragger
                    styles={{ trigger: { border: 0, background: "transparent", textAlign: "left" } }}
                    showUploadList={false}
                    multiple
                    onAttachFiles={(files) => {
                      onChange({
                        ...application,
                        documents: application.documents.concat(
                          AttachDocumentDataConverter.convertDocumentsToFormData(
                            files.map((x) => ({ id: undefined!, documentId: x.id, documentName: x.name })),
                            host,
                          ),
                        ),
                      });
                      onCommit?.();
                    }}
                    content={
                      <Button size="small" icon={<PaperClipOutlined />} tabIndex={-1}>
                        {t("attach_files")}
                      </Button>
                    }
                  />
                )}
                <Space orientation="vertical" size={2}>
                  {application.documents.map((x, index) => (
                    <Space key={index} align="center">
                      {viewSettings ? (
                        <span>{x.name}</span>
                      ) : (
                        <a target="_blank" href={getDocumentUrl(x.response?.id!, host)} rel="noreferrer">
                          {x.name}
                        </a>
                      )}
                      {canEditApplicationFields && (
                        <Button
                          icon={<DeleteOutlined style={{ fontSize: 12 }} />}
                          danger
                          type="text"
                          style={{ height: 16, width: 16 }}
                          onClick={() => {
                            const copy = _.clone(application.documents);
                            copy.splice(index, 1);

                            onChange({
                              ...application,
                              documents: copy,
                            });
                            onCommit?.();
                          }}
                        />
                      )}
                    </Space>
                  ))}
                </Space>
              </>
            ),
          },
        ]
          .flat(3)
          .filter((item) => !!item)}
      />
    </ConfigProvider>
  );
});
export default ApplicationDocuments;
